import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Drawer } from './ui/Drawer';
import { TanTable } from './TanTable';
import ordersService, { StreetTurnOpportunity } from '../services/orders.service';
import { useToast } from '../contexts/ToastContext';
import { ProNumberLink } from '../utils/orderCardHelpers';

interface ReuseContainerDrawerProps {
  /** The orig (import) pro that's being reused FROM, or the export pro
   *  that's viewing its candidate imports (read-only mode). */
  pronumber: number;
  /** Import's current equiptype — used to flag candidates whose equiptype
   *  will be rewritten when the reuse fires (so dispatchers know they're
   *  changing the export's booking type). Import mode only. */
  importEquipType?: string;
  /** 'import' (default) initiates a street turn from the import pro;
   *  'export' is read-only and shows the candidate imports for an export. */
  mode?: 'import' | 'export';
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful reuse so the parent can refresh order details.
   *  Not invoked in 'export' mode (read-only). */
  onSuccess?: () => void;
  /** Runs the order save flow at the reuse commit point and resolves `true`
   *  only when it's safe to proceed (saved cleanly, or nothing to save). The
   *  reuse is aborted if it resolves `false`. Saving up front means the reuse
   *  runs against the just-saved data — see the legacy parity note in
   *  OrderReuseActions. Import mode only; omit to reuse without saving. */
  onSaveBeforeReuse?: () => Promise<boolean>;
}

/**
 * Street-turn opportunities drawer. In 'import' mode, initiates a Street Turn
 * from the orig (import) pro — lists candidate export pros from
 * /streetturn/import/{pronumber} with a Reuse action and a manual-entry
 * fallback. In 'export' mode, lists the candidate import pros that could
 * feed this export (from /streetturn/export/{pronumber}) for view-only.
 */
const ReuseContainerDrawer: React.FC<ReuseContainerDrawerProps> = ({
  pronumber,
  importEquipType,
  mode = 'import',
  isOpen,
  onClose,
  onSuccess,
  onSaveBeforeReuse,
}) => {
  const isExportMode = mode === 'export';
  const { showToast } = useToast();
  const [candidates, setCandidates] = useState<StreetTurnOpportunity[]>([]);
  // A failed lookup must not tell the user "no opportunities — use manual
  // entry" when the street-turn service merely hiccupped (2026-08-25 sweep).
  const [candidatesFailed, setCandidatesFailed] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [manualPro, setManualPro] = useState('');
  const [submittingPro, setSubmittingPro] = useState<number | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<number | null>(null);
  // Native <dialog> for the reuse confirmation so it stacks above this
  // drawer (which is itself a modal <dialog>) in the browser's top layer.
  const confirmDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dlg = confirmDialogRef.current;
    if (!dlg) return;
    if (confirmTarget !== null && !dlg.open) dlg.showModal();
    else if (confirmTarget === null && dlg.open) dlg.close();
  }, [confirmTarget]);
  // Portal target for TanTable's menus/popovers. Drawer renders via
  // dialog.showModal(), which puts its content in the browser's top layer —
  // createPortal to document.body lands in the normal layer and is hidden
  // under the dialog regardless of z-index. We need a target inside the
  // dialog. But the Drawer's *panel* div has `transform` (slide animation),
  // which creates a containing block for position:fixed descendants and
  // throws off the popovers' top/left coords. So portal to the <dialog>
  // itself (no transform) — walk up from the table wrapper to find it, which
  // also stays correct when this drawer opens on top of another <dialog>.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const setPortalAnchor = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      setPortalContainer(null);
      return;
    }
    let current: HTMLElement | null = node.parentElement;
    while (current && current.tagName !== 'DIALOG') {
      current = current.parentElement;
    }
    setPortalContainer(current);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMessages([]);
    setManualPro('');

    let cancelled = false;
    setLoadingCandidates(true);
    setCandidatesFailed(false);
    const fetcher = isExportMode
      ? ordersService.getExportStreetTurn(pronumber)
      : ordersService.getImportStreetTurn(pronumber);
    fetcher
      .then((res) => {
        if (cancelled) return;
        setCandidates(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch street-turn candidates:', err);
        setCandidates([]);
        setCandidatesFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, pronumber, isExportMode]);

  const submitReuse = useCallback(
    async (reusepro: number) => {
      setSubmittingPro(reusepro);
      setErrorMessages([]);
      // Commit point. Close the confirm popup and the drawer so the user lands
      // back on the pro; the outcome is reported via a toast. Closing first
      // also keeps any save-time prompt (deadline / driver override — a
      // normal-layer portal) from being trapped behind these native top-layer
      // <dialog>s.
      setConfirmTarget(null);
      onClose();

      // Save the order's pending edits first; abort the reuse if that save is
      // blocked (the save flow surfaces its own error toast) so the reuse never
      // runs against rejected edits. On success it runs against the just-saved
      // data — the backend reloads the pro from the DB.
      if (onSaveBeforeReuse) {
        const saved = await onSaveBeforeReuse();
        if (!saved) {
          setSubmittingPro(null);
          return;
        }
      }

      try {
        await ordersService.reuseContainer(pronumber, reusepro);
        showToast({
          type: 'success',
          message: `PRO ${pronumber} reused on PRO ${reusepro}`,
        });
        onSuccess?.();
        window.open(
          `/searchresults?q=${pronumber},${reusepro}&multi=true`,
          '_blank',
          'noopener,noreferrer',
        );
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        let msgs: string[];
        if (detail?.errors && Array.isArray(detail.errors)) {
          msgs = detail.errors;
        } else if (typeof detail === 'string') {
          msgs = [detail];
        } else {
          msgs = [err?.message || 'Reuse failed'];
        }
        // Drawer is already closed at this point, so report via toast rather
        // than the in-drawer banner.
        showToast({ type: 'error', message: `Reuse failed: ${msgs.join('; ')}` });
      } finally {
        setSubmittingPro(null);
      }
    },
    [pronumber, onClose, onSuccess, showToast, onSaveBeforeReuse],
  );

  const handleManualSubmit = () => {
    const parsed = parseInt(manualPro.trim(), 10);
    if (!parsed || Number.isNaN(parsed)) {
      setErrorMessages(['Enter a valid pronumber']);
      return;
    }
    if (parsed === pronumber) {
      setErrorMessages(['Cannot reuse onto the same pro']);
      return;
    }
    setConfirmTarget(parsed);
  };

  const handleConfirmReuse = useCallback(async () => {
    if (confirmTarget === null) return;
    await submitReuse(confirmTarget);
    // submitReuse closes/navigates on success and populates errorMessages on
    // failure — either way we want the confirm modal gone afterwards.
    setConfirmTarget(null);
  }, [confirmTarget, submitReuse]);

  const willRewriteType = useCallback(
    (c: StreetTurnOpportunity) =>
      Boolean(importEquipType) &&
      Boolean(c.equipType) &&
      c.equipType !== importEquipType,
    [importEquipType],
  );

  const columns = useMemo<ColumnDef<StreetTurnOpportunity>[]>(() => {
    if (isExportMode) {
      return [
        {
          accessorKey: 'importPronumber',
          header: 'Pro #',
          cell: (info) => {
            const pro = info.getValue() as number | undefined;
            return pro ? <ProNumberLink pronumber={pro} target="search" /> : '-';
          },
          size: 90,
        },
        {
          accessorKey: 'customer',
          header: 'Customer',
          cell: (info) => (info.getValue() as string) || '-',
          size: 180,
        },
        {
          accessorKey: 'dropReleaseDatetime',
          header: 'Drop Rel',
          cell: (info) => (info.getValue() as string) || '-',
          size: 110,
        },
        {
          accessorKey: 'currentLocation',
          header: 'Curr Loc',
          cell: (info) => (info.getValue() as string) || '-',
          size: 160,
        },
        {
          accessorKey: 'driver',
          header: 'Driver',
          cell: (info) => (info.getValue() as string) || '-',
          size: 90,
        },
        {
          accessorKey: 'termLocation',
          header: 'Term Loc',
          cell: (info) => (info.getValue() as string) || '-',
          size: 160,
        },
        {
          accessorKey: 'destApptDate',
          header: 'Appt Date',
          cell: (info) => (info.getValue() as string) || '-',
          size: 100,
        },
        {
          accessorKey: 'perDiemStartDate',
          header: 'Per Diem',
          cell: (info) => (info.getValue() as string) || '-',
          size: 100,
        },
        {
          accessorKey: 'estDistance',
          header: 'Est Dist',
          cell: (info) => {
            const v = info.getValue() as number | undefined;
            return v?.toFixed(1) ?? '0';
          },
          size: 90,
        },
      ];
    }

    return [
      {
        accessorKey: 'exportPronumber',
        header: 'Pro #',
        cell: (info) => {
          const pro = info.getValue() as number | undefined;
          return pro ? <ProNumberLink pronumber={pro} target="search" /> : '-';
        },
        size: 90,
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        cell: (info) => (info.getValue() as string) || '-',
        size: 180,
      },
      {
        accessorKey: 'deliveryLocation',
        header: 'Deliv Loc',
        cell: (info) => (info.getValue() as string) || '-',
        size: 160,
      },
      {
        accessorKey: 'legDrop',
        header: 'Drop',
        cell: (info) => (info.getValue() as string) || '-',
        size: 80,
      },
      {
        accessorKey: 'driver',
        header: 'Driver',
        cell: (info) => (info.getValue() as string) || '-',
        size: 90,
      },
      {
        accessorKey: 'origApptDate',
        header: 'Appt Date',
        cell: (info) => (info.getValue() as string) || '-',
        size: 100,
      },
      {
        accessorKey: 'deliveryOption1Date',
        header: 'Cust Target',
        cell: (info) => (info.getValue() as string) || '-',
        size: 100,
      },
      {
        accessorKey: 'firstRcvDate',
        header: 'First RCV',
        cell: (info) => (info.getValue() as string) || '-',
        size: 100,
      },
      {
        accessorKey: 'equipType',
        header: 'Equip Type',
        cell: (info) => {
          const c = info.row.original;
          return (
            <span className="inline-flex items-center gap-1">
              {c.equipType || '-'}
              {willRewriteType(c) && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                  title={`Reuse will rewrite this export's equiptype from ${c.equipType} to ${importEquipType}`}
                >
                  → {importEquipType}
                </span>
              )}
            </span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'estDistance',
        header: 'Est Dist',
        cell: (info) => {
          const v = info.getValue() as number | undefined;
          return v?.toFixed(1) ?? '0';
        },
        size: 90,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const c = info.row.original;
          if (!c.exportPronumber) return null;
          return (
            <button
              type="button"
              disabled={submittingPro !== null}
              onClick={() => setConfirmTarget(c.exportPronumber!)}
              className="px-3 py-1 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded"
            >
              {submittingPro === c.exportPronumber ? 'Reusing…' : 'Reuse'}
            </button>
          );
        },
        size: 90,
      },
    ];
  }, [isExportMode, importEquipType, submittingPro, submitReuse, willRewriteType]);

  const hasRewrites = !isExportMode && candidates.some(willRewriteType);
  const emptyMessage = candidatesFailed
    ? 'Couldn\'t load street turn opportunities — the request failed. Close and reopen to retry.'
    : isExportMode
      ? 'No matching street turn opportunities for this export.'
      : 'No matching opportunities found. Use manual entry below.';
  const title = isExportMode
    ? `Reuse Options — PRO ${pronumber}`
    : `Reuse Container — PRO ${pronumber}`;

  return (
    <>
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="1100px"
    >
      <div className="space-y-4 p-4">
        {!isExportMode && errorMessages.length > 0 && (
          <div className="rounded border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950 p-3">
            <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
              Could not reuse:
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300">
              {errorMessages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <section>
          <h4 className="text-sm font-semibold text-heading mb-2">
            Street Turn Opportunities
          </h4>
          {!loadingCandidates && candidates.length === 0 ? (
            <p className="text-sm text-muted">
              {emptyMessage}
            </p>
          ) : (
            <div ref={setPortalAnchor}>
              <TanTable
                data={candidates}
                columns={columns}
                loading={loadingCandidates}
                settingsKey={`reuse-container-drawer-${mode}-${pronumber}`}
                searchPlaceholder="Search opportunities..."
                defaultSorting={[{ id: 'estDistance', desc: false }]}
                enableColumnFilters
                portalContainer={portalContainer}
                lockedColumns={
                  isExportMode
                    ? [{ id: 'importPronumber', side: 'left' }]
                    : [
                        { id: 'exportPronumber', side: 'left' },
                        { id: 'actions', side: 'right' },
                      ]
                }
              />
              {hasRewrites && (
                <p className="text-xs text-muted mt-2 px-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 mr-1">
                    →
                  </span>
                  flagged exports will have their booking equiptype rewritten
                  to <strong>{importEquipType}</strong> on reuse. Undo restores
                  the original.
                </p>
              )}
            </div>
          )}
        </section>

        {!isExportMode && (
          <section className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-heading mb-2">
              Manual Entry
            </h4>
            <p className="text-xs text-muted mb-2">
              Use this if the export PRO isn't on the opportunities list yet.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={manualPro}
                onChange={(e) => setManualPro(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="Export PRO number"
                className="flex-1 text-sm bg-panel border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-heading"
              />
              <button
                type="button"
                disabled={submittingPro !== null || !manualPro.trim()}
                onClick={handleManualSubmit}
                className="px-3 py-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded"
              >
                {submittingPro && submittingPro === parseInt(manualPro, 10)
                  ? 'Reusing…'
                  : 'Reuse'}
              </button>
            </div>
          </section>
        )}
      </div>
    </Drawer>

    {/* Reuse confirmation — native <dialog> so it stacks above the Drawer
        in the top layer. Submitting state piggybacks on submittingPro. */}
    <dialog
      ref={confirmDialogRef}
      onCancel={(e) => {
        e.preventDefault();
        if (submittingPro === null) setConfirmTarget(null);
      }}
      className="bg-transparent p-0 backdrop:bg-gray-900/50 backdrop:backdrop-blur-sm rounded-lg"
    >
      {confirmTarget !== null && (
        <div className="bg-panel rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 className="text-base font-semibold text-heading mb-2">
            Reuse Container
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This will street-turn the container from PRO {pronumber} onto
            PRO {confirmTarget}. The container, chassis, and termination point
            will be transferred to the export pro. Continue?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setConfirmTarget(null)}
              disabled={submittingPro !== null}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReuse}
              disabled={submittingPro !== null}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-md"
            >
              {submittingPro === confirmTarget ? 'Reusing…' : 'Reuse'}
            </button>
          </div>
        </div>
      )}
    </dialog>
    </>
  );
};

export default ReuseContainerDrawer;
